import React, { useMemo, useState } from 'react';
import { Button, Dropdown, Icon, Input, Label, Loader, Message, Modal } from 'semantic-ui-react';
import { format } from 'date-fns';

import { PageContent, PageSection } from 'common/styles/page';
import { HeaderCell, Table, TableBody, TableCell, TableHeader, TableRow } from 'common/components/Table';
import {
  AuditLogEntry,
  AuditLogOp,
  useListAuditLogQuery,
  useListAuditLogTablesQuery,
} from '../../../../store/generated/admin/audit-log';
import { useApiAdminV1OrganizationListQuery } from '../../../../store/generated/admin/organizations';

const PAGE_SIZE = 50;

const OP_LABEL: Record<AuditLogOp, { label: string; color: 'green' | 'blue' | 'red' }> = {
  I: { label: 'Insert', color: 'green' },
  U: { label: 'Update', color: 'blue' },
  D: { label: 'Delete', color: 'red' },
};

interface Filters {
  organizationId?: number;
  actorAccountId?: string;
  tableName?: string;
  op?: AuditLogOp;
  occurredAtFrom?: string;
  occurredAtTo?: string;
}

const toIsoOrUndefined = (localValue?: string): string | undefined => {
  if (!localValue) return undefined;
  const parsed = new Date(localValue);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
};

const formatTimestamp = (iso: string) => format(new Date(iso), 'yyyy-MM-dd HH:mm:ss');

const ActivityLog: React.FC = () => {
  const [filters, setFilters] = useState<Filters>({});
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AuditLogEntry | null>(null);

  const queryArg = useMemo(() => {
    const actorId = filters.actorAccountId ? Number(filters.actorAccountId) : undefined;
    return {
      organization_id: filters.organizationId,
      actor_account_id: actorId && !Number.isNaN(actorId) ? actorId : undefined,
      table_name: filters.tableName || undefined,
      op: filters.op,
      occurred_at_from: toIsoOrUndefined(filters.occurredAtFrom),
      occurred_at_to: toIsoOrUndefined(filters.occurredAtTo),
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    };
  }, [filters, page]);

  const { data, isLoading, isFetching, error, refetch } = useListAuditLogQuery(queryArg);
  const { data: tables } = useListAuditLogTablesQuery();
  const { data: organizations } = useApiAdminV1OrganizationListQuery();

  const updateFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({});
    setPage(1);
  };

  const orgOptions = useMemo(() => {
    const opts = (organizations ?? [])
      .filter((o) => o.id != null)
      .map((o) => ({ key: o.id, text: o.name ?? `Org ${o.id}`, value: o.id as number }))
      .sort((a, b) => a.text.localeCompare(b.text));
    return [{ key: 'all-orgs', text: 'All organizations', value: undefined }, ...opts];
  }, [organizations]);

  const tableOptions = useMemo(() => {
    const opts = (tables ?? []).map((t) => ({ key: t, text: t, value: t }));
    return [{ key: 'all-tables', text: 'All tables', value: undefined }, ...opts];
  }, [tables]);

  const opOptions = [
    { key: 'all-ops', text: 'All operations', value: undefined },
    { key: 'I', text: 'Insert', value: 'I' },
    { key: 'U', text: 'Update', value: 'U' },
    { key: 'D', text: 'Delete', value: 'D' },
  ];

  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;

  return (
    <PageContent data-testid="admin-activity-log-page">
      <PageSection padbottom display="flex" flexDirection="row" gap="10px" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
        <Dropdown
          selection
          search
          placeholder="Organization"
          value={filters.organizationId}
          onChange={(_, { value }) =>
            updateFilter('organizationId', value === undefined ? undefined : Number(value))
          }
          options={orgOptions as any}
          style={{ minWidth: 220 }}
        />
        <Dropdown
          selection
          search
          placeholder="Table"
          value={filters.tableName}
          onChange={(_, { value }) =>
            updateFilter('tableName', (value as string) || undefined)
          }
          options={tableOptions as any}
          style={{ minWidth: 200 }}
        />
        <Dropdown
          selection
          placeholder="Operation"
          value={filters.op}
          onChange={(_, { value }) =>
            updateFilter('op', (value as AuditLogOp) || undefined)
          }
          options={opOptions as any}
        />
        <Input
          placeholder="Actor account ID"
          value={filters.actorAccountId ?? ''}
          onChange={(e) => updateFilter('actorAccountId', e.target.value || undefined)}
          style={{ width: 160 }}
        />
        <Input
          type="datetime-local"
          value={filters.occurredAtFrom ?? ''}
          onChange={(e) => updateFilter('occurredAtFrom', e.target.value || undefined)}
          title="From"
        />
        <Input
          type="datetime-local"
          value={filters.occurredAtTo ?? ''}
          onChange={(e) => updateFilter('occurredAtTo', e.target.value || undefined)}
          title="To"
        />
        <Button basic onClick={clearFilters}>Clear</Button>
        <Button icon basic title="Refresh" aria-label="Refresh" onClick={() => refetch()}>
          <Icon name="refresh" />
        </Button>
      </PageSection>

      {error && (
        <Message negative>
          <Message.Header>Error loading audit log</Message.Header>
          <p>The request failed. You may need to be a super admin.</p>
        </Message>
      )}

      {isLoading ? (
        <Loader active size="large" />
      ) : (
        <PageSection>
          <div style={{ overflowX: 'auto', opacity: isFetching ? 0.6 : 1 }}>
            <Table>
              <TableHeader>
                <TableRow>
                  <HeaderCell>Time</HeaderCell>
                  <HeaderCell>Actor</HeaderCell>
                  <HeaderCell>Organization</HeaderCell>
                  <HeaderCell>Op</HeaderCell>
                  <HeaderCell>Table</HeaderCell>
                  <HeaderCell>Row</HeaderCell>
                  <HeaderCell>Capture</HeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.results ?? []).map((row) => {
                  const op = OP_LABEL[row.op];
                  const compromised = row.capture_status !== 'ok';
                  return (
                    <TableRow
                      key={row.id}
                      onClick={() => setSelected(row)}
                      style={{
                        cursor: 'pointer',
                        background: compromised ? '#fff3f3' : undefined,
                      }}
                    >
                      <TableCell $firstcol style={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {formatTimestamp(row.occurred_at)}
                      </TableCell>
                      <TableCell>
                        {row.actor
                          ? <>{row.actor.name} <span style={{ color: '#999' }}>({row.actor.email})</span></>
                          : <em style={{ color: '#999' }}>system</em>}
                        {row.on_behalf_of && (
                          <div style={{ fontSize: 11, color: '#a55' }}>
                            on behalf of {row.on_behalf_of.name}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{row.organization?.name ?? <span style={{ color: '#999' }}>—</span>}</TableCell>
                      <TableCell>
                        <Label color={op.color} size="tiny">{op.label}</Label>
                      </TableCell>
                      <TableCell style={{ fontFamily: 'monospace', fontSize: 12 }}>{row.table_name}</TableCell>
                      <TableCell style={{ fontFamily: 'monospace', fontSize: 12 }}>{row.row_id}</TableCell>
                      <TableCell>
                        {compromised ? (
                          <Label
                            color="red"
                            size="small"
                            title="This audit event is compromised — before/after may be incomplete."
                          >
                            <Icon name="warning sign" />
                            {row.capture_status === 'partial' ? 'Compromised' : 'Failed'}
                          </Label>
                        ) : (
                          <span style={{ color: '#999' }}>ok</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {data && data.results.length === 0 && (
            <Message info>No audit entries match these filters.</Message>
          )}

          {data && data.count > PAGE_SIZE && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
              <Button size="small" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Previous
              </Button>
              <span style={{ fontSize: 14, color: '#666' }}>
                Page {page} of {totalPages} ({data.count.toLocaleString()} entries)
              </span>
              <Button size="small" disabled={!data.next} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </div>
          )}
        </PageSection>
      )}

      <DetailModal entry={selected} onClose={() => setSelected(null)} />
    </PageContent>
  );
};

interface DetailModalProps {
  entry: AuditLogEntry | null;
  onClose: () => void;
}

const DetailModal: React.FC<DetailModalProps> = ({ entry, onClose }) => {
  if (!entry) return null;
  const op = OP_LABEL[entry.op];
  const compromised = entry.capture_status !== 'ok';
  return (
    <Modal open onClose={onClose} size="large">
      <Modal.Header>
        <Label color={op.color}>{op.label}</Label>{' '}
        <span style={{ fontFamily: 'monospace' }}>{entry.table_name}</span>
        {' / '}
        <span style={{ fontFamily: 'monospace' }}>{entry.row_id}</span>
      </Modal.Header>
      <Modal.Content scrolling>
        {compromised && (
          <Message negative icon>
            <Icon name="warning sign" />
            <Message.Content>
              <Message.Header>This audit event is compromised</Message.Header>
              Capture status: <b>{entry.capture_status}</b>. The before/after snapshots below may
              be incomplete or missing — treat this row as evidence that a change occurred, but
              not as a reliable record of what changed.
            </Message.Content>
          </Message>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13, marginBottom: 16 }}>
          <div><b>When:</b> {formatTimestamp(entry.occurred_at)}</div>
          <div><b>Actor:</b> {entry.actor ? `${entry.actor.name} (${entry.actor.email})` : 'system'}</div>
          <div><b>Organization:</b> {entry.organization?.name ?? '—'}</div>
          <div><b>IP:</b> {entry.ip_address ?? '—'}</div>
          <div><b>Request:</b> <span style={{ fontFamily: 'monospace' }}>{entry.request_id ?? '—'}</span></div>
          {entry.on_behalf_of && (
            <div style={{ gridColumn: '1 / -1' }}>
              <b>On behalf of:</b> {entry.on_behalf_of.name} ({entry.on_behalf_of.email})
              {entry.impersonation_session_id != null && (
                <> · session #{entry.impersonation_session_id}</>
              )}
            </div>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <JsonPanel title={entry.op === 'U' ? 'Before (changed fields only)' : 'Before'} value={entry.before} />
          <JsonPanel title="After" value={entry.after} />
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Close</Button>
      </Modal.Actions>
    </Modal>
  );
};

const JsonPanel: React.FC<{ title: string; value: Record<string, unknown> | null }> = ({ title, value }) => (
  <div>
    <div style={{ fontWeight: 600, marginBottom: 4 }}>{title}</div>
    <pre style={{
      background: '#f7f7f9',
      border: '1px solid #eee',
      padding: 12,
      borderRadius: 4,
      fontSize: 12,
      maxHeight: 480,
      overflow: 'auto',
      margin: 0,
    }}>
      {value === null ? '—' : JSON.stringify(value, null, 2)}
    </pre>
  </div>
);

export default ActivityLog;
